import { HelloResponse } from '@globals/types/Hello';
import serverFetch from './ServerFetch';
import { usePromoMessagesStore } from '@customer/stores/promoMessages';
import { useImportantMessageStore } from '@customer/stores/importantMessage';
import { useCustomerRfpListStore } from '@customer/stores/customerRfpList';
import { useUserStore } from '@globals/stores/user';
import { useAppStore } from '@globals/stores/app';
import { useBuyerMessagesStore } from '@buyer/stores/buyerMessagesStore';
import { useServicesStore } from '@customer/stores/services';
import { useCustomerOnboardingStore } from '@customer/stores/customerOnboarding';

/**
 * Pobiera początkowe dane z serwera przy użyciu wywołania `hello.ofer`.
 */
async function getRsvp() {
	const { pathname, search, hostname } = document.location;
	const query = new URLSearchParams(search);

	// Jeżeli użytkownik, chce wejść na stronę Zapytań lub Kontaktów, dodaj parametr
	// informujący o tym, żeby nie wyświetlać mu ekranów onboardingowych.
	if (!pathname.includes('zapytania') && !pathname.includes('kontakty') && !pathname.endsWith('/konto/') && !pathname.endsWith('/konto')) {
		query.set('disableOnboarding', 'true');
	}

	// Przed pobraniem danych z hello.ofer, musimy wywołać `init-chat.ofer,
	// jeśli chcemy, by użytkownik został wlogowany. Struktura takiego adresu
	// wyglada następująco: /moje-zapytania/rfp/:rfpId/:chatId/czat?obj=xxxxxx...
	if (query.has('obj') && pathname.includes('moje-zapytania') && pathname.includes('rfp')) {
		const pathSegments = pathname.split('/');
		await fetch(`/rfp-json/init-chat.ofer?rfpId=${pathSegments[3]}&cid=${pathSegments[4]}&${query.toString()}`);
	}

	// Utwórz placeholder odpowiedzi serwera.
	let rsvp: HelloResponse = {
		baseUrl: hostname,
		flags: [],
		error: 'NOT_AUTH',
		status: 'ERROR',
		sessionId: '',
	};

	rsvp = await serverFetch(`/profile/hello.ofer?${query.toString()}`);

	// Nie rozumiem tego. Z jakiegoś powodu wywołujemy hello.ofer jeszcze raz,
	// gdy aplikacja jest aplikacją kupującego i odpowiedź z serwera jest 'NOT_AUTH'.
	const isBuyerApp = window.localStorage.getItem('isBuyerApp') === 'true';
	if (isBuyerApp && rsvp.error === 'NOT_AUTH') {
		rsvp = await serverFetch(`/profile/hello.ofer?${query}`, {}, 'POST');
	}

	return rsvp;
}

/**
 * Czy użytkownik powinien zostać przekierowany na stronę logowania,
 * jeżeli nie ma wystarczających uprawnień?
 */
function needsCustomerLogin(role = '', requireCustomerLogin = false) {
	return (!role || role === 'BUYER') && requireCustomerLogin;
}

/**
 * Inicjuje odpowiednie Store'y na podstawie odpowiedzi serwera.
 */
async function initStores(rsvp: HelloResponse) {
	const { role } = rsvp;
	const isCustomer = role === 'ADMIN' || role === 'CUSTOMER';
	const isBuyer = role === 'BUYER';

	const appStore = useAppStore();
	appStore.sessionId = rsvp.sessionId;

	const userStore = useUserStore();
	userStore.update(rsvp);

	if (isCustomer) {
		const promoMessagesStore = usePromoMessagesStore();
		promoMessagesStore.update();

		const importantMessageStore = useImportantMessageStore();
		importantMessageStore.update(rsvp);

		const customerRfpListStore = useCustomerRfpListStore();
		customerRfpListStore.updateFlags(rsvp.flags);

		const customerOnboardingStore = useCustomerOnboardingStore();
		customerOnboardingStore.update(rsvp);

		if (rsvp.apImportantMessage) {
			const servicesStore = useServicesStore();
			servicesStore.setNotifications(rsvp.apImportantMessage);
		}
	}

	if (isBuyer) { // było jeszcze query.indexOf('formularz') === -1, ale to nigdy nie działało.
		const buyerMessagesStore = useBuyerMessagesStore();
		buyerMessagesStore.update();
		buyerMessagesStore.checkIfBellIsRinging();
	}

	appStore.update(rsvp);
}

/**
 * Pobiera informacje o użytkownika i inicjuje Store'y wymagane do działania aplikacji.
 * @param requireCustomerLogin Czy użytkownik musi być zalogowanym użytkownikiem z rolą `CUSTOMER` (lub wyżej), aby przejść dalej?
 * @returns Czy potrzebne jest przekierowanie na stronę logowania lub inny adres URL?
 */
export default async function hello(requireCustomerLogin = false) {
	const rsvp = await getRsvp();
	const needsLogin = needsCustomerLogin(rsvp.role, requireCustomerLogin);
	initStores(rsvp);
	return needsLogin;
}
